import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { certificationActions } from 'store/slices/certification/certificationSilce';

/**
 * @hook useTimer
 * @description
 * Hook personalizado que maneja un temporizador basado en Redux. Se sincroniza con el estado global
 * para controlar si el temporizador debe iniciarse, detenerse o reiniciarse. Al finalizar el conteo,
 * despacha acciones para marcar el fin del tiempo (`setTimeOver`) y detener el temporizador (`setStartTimer(false)`).
 *
 * Este hook también escucha cambios en el tiempo inicial para reiniciarse si es necesario.
 *
 * @returns {{ time: number }} - Retorna el tiempo restante actual.
 */
const useTimer = () => {
	const dispatch = useDispatch();

	// Estado global de Redux
	const { remainingTime: initialTime, startTimer } = useSelector((state) => state.certificationReducer);

	// Estado local para mostrar el tiempo restante
	const [time, setTime] = useState(initialTime);

	/**
	 * useEffect para iniciar el temporizador cuando `startTimer` es true.
	 * También reinicia el estado `timeOver` en Redux.
	 */
	useEffect(() => {
		let timerInterval;

		dispatch(certificationActions.setTimeOver(false)); // Reinicia timeOver en Redux

		if (startTimer) {
			timerInterval = setInterval(() => {
				setTime((prevTime) => {
					if (prevTime > 0) {
						return prevTime - 1;
					} else {
						clearInterval(timerInterval);
						dispatch(certificationActions.setTimeOver(true));
						dispatch(certificationActions.setStartTimer(false));
						return 0;
					}
				});
			}, 1000);
		} else {
			clearInterval(timerInterval); // Detener si se cancela manualmente
		}

		// Limpieza del intervalo al desmontar o reiniciar
		return () => clearInterval(timerInterval);
	}, [startTimer, dispatch]);

	/**
	 * useEffect para actualizar el tiempo cuando cambia `initialTime`.
	 * Esto es útil si el tiempo inicial se modifica desde el exterior.
	 */
	useEffect(() => {
		if (startTimer) {
			setTime(initialTime);
		}
	}, [initialTime, startTimer]);

	return { time };
};

export default useTimer;